import firebase from "firebase/app"
import { firestoreAction } from "vuexfire"
import componentsAPI from "../../api/components"

// components list
const components = ["users", "orgs"]

/**
 * Init state by component
 *
 * @param {string} component
 */
const initialState = (component) => ({ [component]: [], currentDoc: {} })

/**
 * Generate state.
 *
 * @param {string} component - Component name.
 */
const genState = (component) => initialState(component)

/**
 * Generate getters.
 *
 * @param {string} component - Component name.
 */
const genGetters = (component) => ({
  // get all component data from state
  get: (state) => state[component],

  // get specific doc id
  getDoc: (state) => state.currentDoc
})

/**
 * Init mutations by component
 *
 * @param {string} component
 */
const genMutations = (component) => ({
  // reset state
  reset: (state) => {
    const newState = initialState(component)
    Object.entries(newState).forEach(([key, value]) => {
      state[key] = value
    })
  }
})

/**
 * Init actions by component.
 *
 * @param {string} component
 */
const genActions = (component) => ({
  // create new component
  add: async (context, payload) => {
    // init api
    const jwt = await firebase.auth().currentUser.getIdToken()
    const api = componentsAPI[component](jwt)

    // create components
    return api.create(payload)
  },

  // update component
  update: async (context, { id, data, options = {} }) => {
    // init api
    const jwt = await firebase.auth().currentUser.getIdToken()
    const api = componentsAPI[component](jwt)

    // create components
    return api.update(id, data, options)
  },

  // delete component
  delete: async (context, id) => {
    // init api
    const jwt = await firebase.auth().currentUser.getIdToken()
    const api = componentsAPI[component](jwt)

    // create components
    return api.delete(id)
  },

  // delete component
  removeIntegration: async (context, { data }) => {
    // init api
    const jwt = await firebase.auth().currentUser.getIdToken()
    const api = componentsAPI[component](jwt)

    // create components
    return api.removeIntegration(data)
  },

  // bind document
  bindDoc: firestoreAction(({ bindFirestoreRef }, id) => {
    // get base path
    const db = firebase.firestore()
    const ref = db.doc(`${component}/${id}`)

    bindFirestoreRef("currentDoc", ref)
  }),

  // unbind document
  unbindDoc: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef("currentDoc")
  }),

  // bind component data
  bind: firestoreAction(
    (
      { bindFirestoreRef },
      { constraints, orderBy, limit, startAfter, startAt, endBefore } = {
        limit: 200
      }
    ) => {
      const db = firebase.firestore()

      let query = db.collection(component)
      // map constraints
      if (constraints) {
        constraints.forEach((constraint) => {
          query = query.where(...constraint)
        })
      }
      // set order
      if (orderBy) {
        if (orderBy.constructor !== Array) {
          orderBy = [orderBy]
        }
        query = query.orderBy(...orderBy)
      }
      // start after
      if (startAfter) {
        query = query.startAfter(startAfter)
      }
      // start at
      if (startAt) {
        query = query.startAt(startAt)
      }
      // end before
      if (endBefore) {
        query = query.endBefore(endBefore)
      }
      // set limit
      if (limit) {
        query = query.limit(limit)
      }

      // bind firestore to component
      return bindFirestoreRef(component, query)
    }
  ),

  // unbind component data
  unbind: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef(component)
  })
})

/**
 * Loop over modules
 */
const modules = {}
components.forEach((component) => {
  modules[component] = {
    namespaced: true,
    state: genState(component),
    getters: genGetters(component),
    mutations: genMutations(component),
    actions: genActions(component)
  }
})

export default modules
